import React, { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./SalesAnalyticsPanel.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

export default function SalesAnalyticsPanel() {
  const [shop, setShop] = useState("ALL");
  const [date, setDate] = useState(todayKey());
  const [summary, setSummary] = useState({});
  const [topShops, setTopShops] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [paymentBreakdown, setPaymentBreakdown] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadAnalytics() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `${API_BASE}/api/erp/sales-analytics?shop=${shop}&date=${date}`,
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load sales analytics");
      }

      setSummary(data.summary || {});
      setTopShops(data.topShops || []);
      setTopProducts(data.topProducts || []);
      setPaymentBreakdown(data.paymentBreakdown || []);
    } catch (err) {
      setError(err.message || "Sales analytics loading failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, date]);

  const status = summary.status || "SAME";
  const isUp = status === "UP";
  const isDown = status === "DOWN";

  return (
    <div className="salesAnalytics">
      <div className="salesAnalytics__head">
        <div>
          <span>Sales Analytics</span>
          <h2>Boss Sales Performance</h2>
          <p>
            Daily sales comparison, shop performance, payment breakdown and top
            product ranking.
          </p>
        </div>

        <div className="salesAnalytics__filters">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <LocationSelect value={shop} onChange={setShop} mode="shop" includeAll />

          <button onClick={loadAnalytics} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="salesAnalytics__error">{error}</div>}

      <div className="salesAnalytics__cards">
        <div className="salesMetricCard salesMetricCard--main">
          <span>Today Sales</span>
          <strong>{formatMoney(summary.todaySales)} Ks</strong>
          <small>{formatMoney(summary.todayVouchers)} vouchers</small>
        </div>

        <div className="salesMetricCard">
          <span>Yesterday Sales</span>
          <strong>{formatMoney(summary.yesterdaySales)} Ks</strong>
          <small>{formatMoney(summary.yesterdayVouchers)} vouchers</small>
        </div>

        <div
          className={
            isUp
              ? "salesMetricCard salesMetricCard--up"
              : isDown
                ? "salesMetricCard salesMetricCard--down"
                : "salesMetricCard"
          }
        >
          <span>Sales Change</span>
          <strong>
            {status} {formatPercent(summary.salesChangePercent)}
          </strong>
          <small>{formatMoney(summary.salesChange)} Ks</small>
        </div>

        <div className="salesMetricCard">
          <span>Qty Sold</span>
          <strong>{formatMoney(summary.todayQty)}</strong>
          <small>total item qty</small>
        </div>

        <div className="salesMetricCard">
          <span>Average Bill</span>
          <strong>{formatMoney(summary.todayAverageBill)} Ks</strong>
          <small>sales / voucher</small>
        </div>
      </div>

      <div className="salesAnalytics__grid">
        <div className="salesTableCard">
          <div className="salesTableCard__title">
            <strong>Top Shops Today</strong>
            <small>Best selling shops</small>
          </div>

          <table>
            <thead>
              <tr>
                <th>Shop</th>
                <th>Sales</th>
                <th>Vouchers</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {topShops.length === 0 ? (
                <tr>
                  <td colSpan="4">No shop sales data.</td>
                </tr>
              ) : (
                topShops.map((row) => (
                  <tr key={row.shopCode}>
                    <td>
                      <b>{row.shopCode}</b>
                    </td>
                    <td>{formatMoney(row.sales)} Ks</td>
                    <td>{formatMoney(row.vouchers)}</td>
                    <td>{formatMoney(row.qty)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="salesTableCard">
          <div className="salesTableCard__title">
            <strong>Payment Breakdown</strong>
            <small>Cash / KPay / COD / Bank etc.</small>
          </div>

          <table>
            <thead>
              <tr>
                <th>Payment</th>
                <th>Sales</th>
                <th>Vouchers</th>
              </tr>
            </thead>
            <tbody>
              {paymentBreakdown.length === 0 ? (
                <tr>
                  <td colSpan="3">No payment data.</td>
                </tr>
              ) : (
                paymentBreakdown.map((row) => (
                  <tr key={row.paymentMethod}>
                    <td>
                      <b>{row.paymentMethod}</b>
                    </td>
                    <td>{formatMoney(row.sales)} Ks</td>
                    <td>{formatMoney(row.vouchers)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="salesTableCard salesTableCard--full">
        <div className="salesTableCard__title">
          <strong>Top Products Today</strong>
          <small>Best selling product code, color and size</small>
        </div>

        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Color</th>
              <th>Size</th>
              <th>Qty</th>
              <th>Sales</th>
            </tr>
          </thead>
          <tbody>
            {topProducts.length === 0 ? (
              <tr>
                <td colSpan="5">No product sales data.</td>
              </tr>
            ) : (
              topProducts.map((row, index) => (
                <tr key={`${row.code}-${row.color}-${row.size}-${index}`}>
                  <td>
                    <b>{row.code}</b>
                  </td>
                  <td>{row.color}</td>
                  <td>{row.size}</td>
                  <td>{formatMoney(row.qty)}</td>
                  <td>{formatMoney(row.sales)} Ks</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


